import { useEffect } from "react";
import AppLayout from "@/layouts/AppLayout";
import { Outlet, useLocation } from "react-router-dom";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import TabLayout from "./_TabLayout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";
import useCapabilities, { useCanViewBeneficiariesBool, useDenyAuthorization } from "@/hooks/useCapabilities";
import { useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { usePersonErrorHandling } from "../_components/usePersonErrorHandling";
import PersonNotFoundErrorBoundary from "../_components/PersonNotFoundErrorBoundary";
import { useCanAccessPersonBenefits } from "./_utils";

export default function PersonBenefitsLayout() {
  const { loaded } = useCapabilities();
  const flagsLoaded = useFlagsLoaded();
  const canAccessBenefits = useCanAccessPersonBenefits();
  const canViewBeneficiaries = useCanViewBeneficiariesBool();
  const denyAuthorization = useDenyAuthorization();
  const location = useLocation();
  const isBeneficiariesRoute = location.pathname.includes("/benefits/beneficiaries");
  const isUpdateBeneficiariesPage = location.pathname.includes("/beneficiaries/update/");
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const { pgaId, navLinks, shouldRenderError } = usePersonErrorHandling();

  useEffect(() => {
    // GRP access depends on the commission flag, so wait for flags before denying.
    // Otherwise a GRP-only user is sent home on first paint while flags are empty.
    if (!loaded || !flagsLoaded) return;
    if (!canAccessBenefits) {
      denyAuthorization();
    } else if (isBeneficiariesRoute && !canViewBeneficiaries) {
      // GRP-only users may access the GRP tab but not beneficiary records.
      denyAuthorization();
    }
  }, [loaded, flagsLoaded, canAccessBenefits, isBeneficiariesRoute, canViewBeneficiaries, denyAuthorization]);

  if (shouldRenderError) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <PersonNotFoundErrorBoundary />
      </AppLayout>
    );
  }

  if (isUpdateBeneficiariesPage) {
    return <Outlet />;
  }

  const renderDesktopLayout = () => (
    <>
      <PersonObjectHeading pgaId={pgaId!} />
      <TabLayout>
        <Outlet />
      </TabLayout>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        title="Benefits"
        compactOnly
        compactTitle="Benefits"
        backLink={{ to: "/people/:pgaId", params: { pgaId: pgaId! } }}
      />
      <PersonObjectHeading pgaId={pgaId!} />
      <TabLayout>
        <Outlet />
      </TabLayout>
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}
    </AppLayout>
  );
}
